/**
 * Catalog admin — the management table for the PLATFORM-GLOBAL catalog the template
 * selects draw from: product types (label + the material Generate describes), phone
 * models (brand + model + slug) and case styles (label + code). Mounted in the Editor's "Catalog" tab, beside Brands (which manages the
 * brand catalog the same way).
 *
 * Why it exists: the Wizard/Details "＋ New" buttons cover the add-while-working
 * case, but there was no place to SEE the whole catalog or fix a typo in an
 * existing entry. Add + edit only — entries are referenced by templates, so
 * delete is deliberately not offered here.
 *
 * Admin-gated server-side (POST/PATCH are super-admin); a 403 surfaces as a
 * readable error. Uses only the injected JsonClient via useServices().
 */
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import type { PhoneModelOption, CaseStyleOption, ProductTypeAdmin } from "@genz/platform";
import { HttpError } from "@genz/platform";
import { Accordion } from "../ui/Accordion.js";
import { Icon } from "../ui/Icon.js";
// WS-B B-8 / F-40: this tab already re-fetched its OWN list after a save; the signal tells
// every mounted TEMPLATE list that its server-resolved `display.model` / `display.style` are
// now stale too. Renaming a model here changed the label of every template referencing it.
import { notifyCatalogChanged } from "../templates/catalogInvalidation.js";
import "./catalog-admin.css";
import { RowAction } from "../ui/RowAction.js";

const TPU_CODE = "tpu_phone_case";

/** Stable slug/code from a human label — mirrors the Wizard's new-style derivation. */
function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function readableError(e: unknown): string {
  if (e instanceof HttpError && e.status === 403) return "Only an admin can change the catalog.";
  if (e instanceof HttpError && e.status === 409) return "That slug/code is already taken by another entry.";
  return e instanceof Error ? e.message : String(e);
}

/** One editable row: view mode with an Edit button, or input mode with Save/Cancel. */
function EditableRow(props: {
  testId: string;
  cells: string[];
  fields: { key: string; value: string; ariaLabel: string }[];
  onSave: (values: Record<string, string>) => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const start = () => {
    setValues(Object.fromEntries(props.fields.map((f) => [f.key, f.value])));
    setEditing(true);
  };
  const complete = props.fields.every((f) => (values[f.key] ?? "").trim());
  const save = async () => {
    if (!complete || busy) return;
    setBusy(true);
    try {
      await props.onSave(Object.fromEntries(props.fields.map((f) => [f.key, (values[f.key] ?? "").trim()])));
      setEditing(false);
    } catch (e) {
      props.onError(readableError(e)); // row stays open — typed values survive
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <tr data-testid={props.testId}>
        {props.cells.map((c, i) => <td key={i} className={i > 0 ? "catalog-admin__dim" : undefined}>{c}</td>)}
        <td className="catalog-admin__actions">
          <RowAction label="Edit" icon="editor" tone="quiet" onClick={start}
            data-testid={`${props.testId}-edit`} />
        </td>
      </tr>
    );
  }
  return (
    <tr data-testid={`${props.testId}-editing`}>
      <td colSpan={props.cells.length}>
        <div className="catalog-admin__edit-row">
          {props.fields.map((f, i) => (
            <input
              key={f.key}
              className="input"
              type="text"
              autoFocus={i === 0}
              value={values[f.key] ?? ""}
              aria-label={f.ariaLabel}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void save(); }
                else if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
              }}
              data-testid={`${props.testId}-input-${f.key}`}
            />
          ))}
        </div>
      </td>
      <td className="catalog-admin__actions">
        {/* WS-Z5 rule 7: a ROW action — `.btn--sm` is what the design doc reserves for row
            and toolbar actions, and the section's one primary is the Add in its toolbar.
            WS-Z9: these two commit the row EDITOR, not a page, and they sit in the same
            action cell as the Edit they replace — so they take the same icon shape, and the
            column stays as narrow while a row is open as it is while every row is closed. */}
        <RowAction label={busy ? "Saving…" : "Save"} icon="check" disabled={!complete || busy}
          busy={busy} onClick={() => void save()} data-testid={`${props.testId}-save`} />
        <RowAction label="Cancel" icon="close" tone="quiet" onClick={() => setEditing(false)}
          data-testid={`${props.testId}-cancel`} />
      </td>
    </tr>
  );
}

/** Add-entry strip above a table: N inputs + Add. */
function AddRow(props: {
  testIdBase: string;
  fields: { key: string; placeholder: string; ariaLabel: string }[];
  onAdd: (values: Record<string, string>) => Promise<void>;
  onError: (msg: string) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const complete = props.fields.every((f) => (values[f.key] ?? "").trim());
  const add = async () => {
    if (!complete || busy) return;
    setBusy(true);
    try {
      await props.onAdd(Object.fromEntries(props.fields.map((f) => [f.key, (values[f.key] ?? "").trim()])));
      setValues({});
    } catch (e) {
      props.onError(readableError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="catalog-admin__add">
      {props.fields.map((f) => (
        <input
          key={f.key}
          className="input"
          type="text"
          value={values[f.key] ?? ""}
          placeholder={f.placeholder}
          aria-label={f.ariaLabel}
          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void add(); } }}
          data-testid={`${props.testIdBase}-input-${f.key}`}
        />
      ))}
      <button type="button" className="btn btn--sm btn--primary" disabled={!complete || busy}
        onClick={() => void add()} data-testid={`${props.testIdBase}-add`}>
        <Icon name="plus" size={16} /> {busy ? "Adding…" : "Add"}
      </button>
    </div>
  );
}

export function CatalogAdmin() {
  const { client } = useServices();
  const [models, setModels] = useState<PhoneModelOption[] | null>(null);
  const [styles, setStyles] = useState<CaseStyleOption[] | null>(null);
  /* `undefined` while loading; `null` when this cloud has no product type routes yet. */
  const [types, setTypes] = useState<ProductTypeAdmin[] | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  /* The open section (Z0 fix round F-6, controlled `Accordion`). */
  const [open, setOpen] = useState<string | null>("models");

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [ms, ss, ts] = await Promise.all([
        client.listPhoneModels(), client.listCaseStyles(), client.listProductTypes().catch(() => null),
      ]);
      setModels(ms);
      setStyles(ss);
      setTypes(ts);
    } catch (e) {
      setError(readableError(e));
    }
  }, [client]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <section className="catalog-admin" data-testid="catalog-admin">
      {/* R11's one-title-per-surface rule, applied locally: this page had no title at all.
          An `<h2>`, not an `<h1>` — the Editor shell that hosts this sub-tab owns the one
          `<h1>` on `#editor` (G-35). */}
      <div className="catalog-admin__head">
        <h2 className="panel-title">Catalog</h2>
      </div>
      {error && <p role="alert" data-testid="catalog-admin-error">{error}</p>}

      {/* Rules 7 + 8. Two `<h3>` sections, each with its own add strip, put two
          `.btn--primary` Adds on screen at once, and each row in edit mode added a third.
          The sections are an accordion — one open, one add strip, one primary — and the
          row Save is a row action. Every `catalog-*` testid is unchanged, and a closed
          section's rows stay in the document, so nothing became unreachable. */}
      {/* CONTROLLED (Z0 fix round F-6): the open section is owned here, so adding or editing
          a row — every one of which re-renders this page — cannot move it. */}
      <Accordion
        name="catalog-admin"
        openKey={open}
        onOpenChange={setOpen}
        items={[
          {
            key: "types",
            head: "Product types",
            meta: <span className="faint">what Generate and the template Category offer</span>,
            body: (
              <>
        <AddRow
          testIdBase="catalog-type-new"
          fields={[
            { key: "label", placeholder: 'Name (e.g. "Metal phone case")', ariaLabel: "New product type name" },
            { key: "material", placeholder: 'The product in words (e.g. "an opaque metal phone case with a flat metal back")', ariaLabel: "New product type material" },
          ]}
          onAdd={async (v) => {
            await client.createProductType({ label: v.label!, material: v.material! });
            await refresh();
          }}
          onError={setError}
        />
        {types === undefined ? (
          <p className="faint">Loading…</p>
        ) : types === null ? (
          <p className="faint" data-testid="catalog-types-unavailable">Product types could not be loaded.</p>
        ) : (
          <div className="table-scroll"><table className="table catalog-admin__table">
            <thead><tr><th>Name</th><th title="Permanent identifier — jobs and templates store it">ID</th><th title="Generate describes the product with these words">Material</th><th /></tr></thead>
            <tbody>
              {types.map((t) => {
                // The clear TPU case has its own prompt, so it takes no material.
                const own = t.code === TPU_CODE;
                return (
                  <EditableRow
                    key={t.id}
                    testId={`catalog-type-${t.id}`}
                    cells={[t.active ? t.label : `${t.label} (hidden)`, t.code, own ? "Its own prompt" : t.material]}
                    fields={[
                      { key: "label", value: t.label, ariaLabel: "Product type name" },
                      ...(own ? [] : [{ key: "material", value: t.material, ariaLabel: "Product type material" }]),
                    ]}
                    onSave={async (v) => {
                      await client.updateProductType(t.id, own ? { label: v.label } : { label: v.label, material: v.material });
                      await refresh();
                    }}
                    onError={setError}
                  />
                );
              })}
            </tbody>
          </table></div>
        )}
              </>
            ),
          },
          {
            key: "models",
            head: "Phone models",
            meta: <span className="faint">what the Model selects offer, shared across every customer</span>,
            body: (
              <>
        <AddRow
          testIdBase="catalog-model-new"
          fields={[
            { key: "brand", placeholder: 'Brand (e.g. "Vivo")', ariaLabel: "New model brand" },
            { key: "model", placeholder: 'Model (e.g. "X200 Pro")', ariaLabel: "New model name" },
          ]}
          onAdd={async (v) => {
            await client.createPhoneModel({ brand: v.brand!, model: v.model! });
            notifyCatalogChanged("phoneModel");
            await refresh();
          }}
          onError={setError}
        />
        {models === null ? (
          <p className="faint">Loading…</p>
        ) : models.length === 0 ? (
          <p className="faint" data-testid="catalog-models-empty">
            No phone models yet — add the first one above. New entries appear in every Model dropdown immediately.
          </p>
        ) : (
          <div className="table-scroll"><table className="table catalog-admin__table">
            <thead><tr><th>Brand</th><th>Model</th><th title="Permanent identifier — it keys stored asset paths, so it never changes when you rename">ID</th><th /></tr></thead>
            <tbody>
              {models.map((m) => (
                <EditableRow
                  key={m.id}
                  testId={`catalog-model-${m.id}`}
                  cells={[m.brand, m.model, m.slug]}
                  fields={[
                    { key: "brand", value: m.brand, ariaLabel: "Brand" },
                    { key: "model", value: m.model, ariaLabel: "Model" },
                  ]}
                  onSave={async (v) => {
                    await client.updatePhoneModel(m.id, { brand: v.brand, model: v.model });
                    notifyCatalogChanged("phoneModel");
                    await refresh();
                  }}
                  onError={setError}
                />
              ))}
            </tbody>
          </table></div>
        )}
              </>
            ),
          },
          {
            key: "styles",
            head: "Case styles",
            meta: <span className="faint">what the Style selects offer</span>,
            body: (
              <>
        <AddRow
          testIdBase="catalog-style-new"
          fields={[{ key: "label", placeholder: 'Style name (e.g. "Clear TPU")', ariaLabel: "New style name" }]}
          onAdd={async (v) => {
            await client.createCaseStyle({ code: slugify(v.label!) || `style-${Date.now()}`, label: v.label! });
            notifyCatalogChanged("caseStyle");
            await refresh();
          }}
          onError={setError}
        />
        {styles === null ? (
          <p className="faint">Loading…</p>
        ) : styles.length === 0 ? (
          <p className="faint" data-testid="catalog-styles-empty">
            No case styles yet — add the first one above.
          </p>
        ) : (
          <div className="table-scroll"><table className="table catalog-admin__table">
            <thead><tr><th>Label</th><th>Code</th><th /></tr></thead>
            <tbody>
              {styles.map((s) => (
                <EditableRow
                  key={s.id}
                  testId={`catalog-style-${s.id}`}
                  cells={[s.label, s.code]}
                  fields={[{ key: "label", value: s.label, ariaLabel: "Style label" }]}
                  onSave={async (v) => {
                    await client.updateCaseStyle(s.id, { label: v.label });
                    notifyCatalogChanged("caseStyle");
                    await refresh();
                  }}
                  onError={setError}
                />
              ))}
            </tbody>
          </table></div>
        )}
              </>
            ),
          },
        ]}
      />

      <p className="faint">
        Base type is free-form on each template (no catalog) — set it in the template's
        Details, where a custom value can be typed via the ＋ New button. Brands have their own
        tab.
      </p>
    </section>
  );
}
